import type { LocationWithPrice } from '@optionway/models';
import { PARAM_LEGS_UNSELECTED_AIRPORTS } from '@optionway/models';

type AirportGroup = 'departure' | 'arrival';

interface AirportsFilterStore {
  deselectedDeparture: string[];
  deselectedArrival: string[];
}

const AIRPORTS_FILTER_DEFAULTS: AirportsFilterStore = {
  deselectedDeparture: [],
  deselectedArrival: [],
};

export const useAirportsFilterStore = defineStore('filters/airports', {
  state: (): AirportsFilterStore => ({
    deselectedDeparture: AIRPORTS_FILTER_DEFAULTS.deselectedDeparture,
    deselectedArrival: AIRPORTS_FILTER_DEFAULTS.deselectedArrival,
  }),
  getters: {
    // Only cross-store access allowed in this store: useSearchResultIndexStore().allFilterOptions — read-only, no other getter, no other store, no actions.
    filterLocations(): LocationWithPrice[] {
      return useSearchResultIndexStore().allFilterOptions.byLeg?.[0]?.locations ?? [];
    },
    departureCityCode(): string | undefined {
      return this.filterLocations.find((loc) => loc.cityIataCode)?.cityIataCode;
    },
    arrivalCityCode(): string | undefined {
      return this.filterLocations.find((loc) => loc.cityIataCode && loc.cityIataCode !== this.departureCityCode)
        ?.cityIataCode;
    },
    departureAirports(): LocationWithPrice[] {
      return this.filterLocations.filter((a) => !!a.iataCode && a.cityIataCode === this.departureCityCode);
    },
    arrivalAirports(): LocationWithPrice[] {
      return this.filterLocations.filter((a) => !!a.iataCode && a.cityIataCode === this.arrivalCityCode);
    },
    // Returns raw city IATA code — components apply locationLabels.city() for display.
    departureSectionLabel(): string {
      return this.departureCityCode ?? '';
    },
    // Returns raw city IATA code — components apply locationLabels.city() for display.
    arrivalSectionLabel(): string {
      return this.arrivalCityCode ?? '';
    },
    selectedDeparture(): string[] {
      const deselected = new Set(this.deselectedDeparture);
      return this.departureAirports.flatMap((a) => (a.iataCode && !deselected.has(a.iataCode) ? [a.iataCode] : []));
    },
    selectedArrival(): string[] {
      const deselected = new Set(this.deselectedArrival);
      return this.arrivalAirports.flatMap((a) => (a.iataCode && !deselected.has(a.iataCode) ? [a.iataCode] : []));
    },
    allDepartureSelected(): boolean {
      return this.departureAirports.length === 0 || this.selectedDeparture.length === this.departureAirports.length;
    },
    allArrivalSelected(): boolean {
      return this.arrivalAirports.length === 0 || this.selectedArrival.length === this.arrivalAirports.length;
    },
    hasActiveFilters(): boolean {
      return !this.allDepartureSelected || !this.allArrivalSelected;
    },
    // All airports in a group deselected: the allowed list would be empty, guaranteed 0 results.
    isImpossible(): boolean {
      return (
        (!this.allDepartureSelected && this.selectedDeparture.length === 0) ||
        (!this.allArrivalSelected && this.selectedArrival.length === 0)
      );
    },
    allowedDepartureAirports(): string | undefined {
      return this.allDepartureSelected ? undefined : this.selectedDeparture.join(',');
    },
    allowedArrivalAirports(): string | undefined {
      return this.allArrivalSelected ? undefined : this.selectedArrival.join(',');
    },
    filterQueryString(): string {
      if (!this.hasActiveFilters) return '';
      const parameters = new URLSearchParams();
      parameters.append(PARAM_LEGS_UNSELECTED_AIRPORTS, this.deselectedDeparture.join(','));
      parameters.append(PARAM_LEGS_UNSELECTED_AIRPORTS, this.deselectedArrival.join(','));
      return parameters.toString();
    },
  },
  actions: {
    // legs.unselected_airports is positional: the first occurrence is the departure group, the
    // second the arrival group — each a comma-separated list of IATA codes to deselect.
    applyFromUrlQuery(url: string) {
      this.$reset();
      const parameters = new URL(url, 'https://dummy.com').searchParams;
      const unselected = parameters.getAll(PARAM_LEGS_UNSELECTED_AIRPORTS);
      if (unselected[0] !== undefined) this.deselectedDeparture = parseAirportIataCodeList(unselected[0]);
      if (unselected[1] !== undefined) this.deselectedArrival = parseAirportIataCodeList(unselected[1]);
    },
    toggleAll(group: AirportGroup) {
      if (group === 'departure') {
        this.deselectedDeparture = this.allDepartureSelected
          ? this.departureAirports.flatMap((a) => (a.iataCode ? [a.iataCode] : []))
          : [];
      } else {
        this.deselectedArrival = this.allArrivalSelected
          ? this.arrivalAirports.flatMap((a) => (a.iataCode ? [a.iataCode] : []))
          : [];
      }
    },
    toggleAirport(group: AirportGroup, id: string, value: boolean) {
      if (group === 'departure') {
        this.deselectedDeparture = value
          ? this.deselectedDeparture.filter((c) => c !== id)
          : [...this.deselectedDeparture, id];
      } else {
        this.deselectedArrival = value
          ? this.deselectedArrival.filter((c) => c !== id)
          : [...this.deselectedArrival, id];
      }
    },
    solo(group: AirportGroup, id: string) {
      if (group === 'departure') {
        this.deselectedDeparture = this.departureAirports.flatMap((a) =>
          a.iataCode && a.iataCode !== id ? [a.iataCode] : [],
        );
      } else {
        this.deselectedArrival = this.arrivalAirports.flatMap((a) =>
          a.iataCode && a.iataCode !== id ? [a.iataCode] : [],
        );
      }
    },
  },
});
